import GeckoStore from 'services/gecko-store';
import GeckoLocation from 'services/gecko-location';
import QueryString from 'querystring';
import GeckoAuthService from '../gecko-auth';
import { renderError } from '../gecko-error';
import { useEffect, useState } from 'react';

const successCodes = [200, 201, 202, 204, 206];

const getAPIUrl = () => {
  const storeURL = GeckoStore.redux.get('appState.apiUrl');
  if (storeURL) return storeURL;
  return process.env.REACT_APP_API_URL;
};

const getRequestHeaders = ({ no_json, extra_headers, no_auth }) => {
  let headers = {};
  if (!no_json) {
    headers['Content-Type'] = 'application/json; charset=utf-8';
  }
  if (extra_headers) {
    Object.assign(headers, extra_headers);
  }
  if (no_auth) {
    return Promise.resolve(headers);
  }
  return GeckoAuthService.getToken().then((token) => {
    return Object.assign({}, headers, { Authorization: token });
  });
};

const getResponseContentType = (response) => {
  const headers = [...response.headers];
  const contentTypeHeader = headers.find(([headerName]) => {
    return headerName === 'content-type';
  });
  if (!contentTypeHeader) {
    return null;
  }
  const [_, contentType] = contentTypeHeader;
  return contentType;
};

const getResponseData = (options, response) => {
  if (options.stream) {
    return Promise.resolve(response.body.getReader());
  }
  if (options.raw) {
    return Promise.resolve(response);
  }

  const contentType = getResponseContentType(response);
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return response.text();
};

const makeRequest = (
  url,
  params = {},
  options = {},
  fetcher,
  firstTry = true
) => {
  return getRequestHeaders(options)
    .then((headers) => fetcher(url, params, headers))
    .then((response) => {
      if (response.status === 401) {
        if (firstTry === true) {
          return makeRequest(url, params, options, fetcher, false);
        }
        GeckoLocation.goTo('/logout');
        throw new Error('Unauthorised');
      }
      if (response.status === 204) {
        return true;
      }
      return getResponseData(options, response).then((data) => {
        const isSuccess = successCodes.includes(response.status);
        if (isSuccess) {
          return Promise.resolve(data);
        }
        return Promise.reject(data);
      });
    });
};

const GeckoRequest = {
  post: (url, params, options) => {
    return makeRequest(url, params, options, (url, params, headers) => {
      return fetch(`${getAPIUrl()}${url}`, {
        method: 'POST',
        body: JSON.stringify(params),
        headers,
      });
    });
  },

  get: (url, params, options) => {
    return makeRequest(url, params, options, (url, params, headers) => {
      const queryString =
        params && Object.keys(params).length
          ? `?${QueryString.stringify(params)}`
          : '';
      return fetch(`${getAPIUrl()}${url}${queryString}`, {
        method: 'GET',
        headers,
      });
    });
  },

  delete: (url, params, options) => {
    return makeRequest(url, params, options, (url, params, headers) => {
      return fetch(`${getAPIUrl()}${url}`, {
        method: 'DELETE',
        body: JSON.stringify(params),
        headers,
      });
    });
  },
};

export function useGetRequest(url, params) {
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  useEffect(() => {
    setIsLoading(true);
    GeckoRequest.get(url, params)
      .then(setResult)
      .catch(renderError)
      .finally(() => setIsLoading(false));
  }, [url, JSON.stringify(params)]);
  return [result, isLoading];
}

export default GeckoRequest;
